import { motion } from "framer-motion";
import { ArrowUpRight, Instagram, Youtube, Linkedin } from "lucide-react";
import { DonationDialog } from "@/components/DonationDialog";
import { ContactForm } from "@/components/ContactForm";
import { Link } from "react-router-dom";

const projects = [
  {
    title: "GritForge",
    description: "The complete ecosystem for building discipline, crushing goals, and becoming your unbreakable self.",
    href: "https://grit-forge-spark.lovable.app",
    tag: "DISCIPLINE PLATFORM",
    external: true,
  },
  {
    title: "Routine-Forge",
    description: "Forge powerful daily routines and build lasting habits. A disciplined space for structured productivity and intentional living.",
    href: "/routine",
    tag: "ROUTINE BUILDER",
    external: false,
  },
  {
    title: "Real Words Only",
    description: "Anti-AI text-only forum. No deepfakes, no reels, no ego-bait. Just honest, unfiltered conversation.",
    href: "/feed",
    tag: "TEXT FORUM",
    external: false,
  },
];

const socials = [
  { icon: Instagram, label: "Instagram", href: "#", comingSoon: true },
  { icon: null, label: "X", href: "https://x.com/_RealWordsOnly_", comingSoon: false },
  { icon: Youtube, label: "YouTube", href: "#", comingSoon: true },
  { icon: Linkedin, label: "LinkedIn", href: "#", comingSoon: true },
];

// X logo
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};

const Index = () => {
  return (
    <div className="min-h-screen bg-[hsl(0,0%,4%)] text-[hsl(0,0%,92%)] selection:bg-[hsl(0,75%,50%)] selection:text-white">
      {/* ─── HERO ─── */}
      <section className="relative min-h-screen flex flex-col justify-center items-center px-6 overflow-hidden">
        {/* Subtle grid background */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(hsl(0,0%,50%) 1px, transparent 1px), linear-gradient(90deg, hsl(0,0%,50%) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />

        {/* Accent line */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute top-0 left-0 right-0 h-[2px] bg-[hsl(0,75%,50%)] origin-left"
        />

        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="relative z-10 max-w-4xl mx-auto text-center space-y-8"
        >
          <motion.p
            variants={fadeUp}
            custom={0}
            className="text-xs tracking-[0.35em] uppercase text-[hsl(0,75%,50%)] font-medium"
          >
            Portfolio &amp; Projects
          </motion.p>

          <motion.h1
            variants={fadeUp}
            custom={1}
            className="text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-bold tracking-tighter leading-[0.9]"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Real Words
            <br />
            <span className="text-[hsl(0,75%,50%)]">Only.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            className="text-lg sm:text-xl text-[hsl(0,0%,55%)] max-w-xl mx-auto leading-relaxed"
          >
            Building things that matter. No fluff, no filler — just work that speaks for itself.
          </motion.p>

          <motion.div variants={fadeUp} custom={3}>
            <a
              href="#projects"
              className="inline-flex items-center gap-2 text-sm tracking-widest uppercase border border-[hsl(0,0%,25%)] px-8 py-4 hover:border-[hsl(0,75%,50%)] hover:text-[hsl(0,75%,50%)] transition-all duration-300"
            >
              View Projects <ArrowUpRight className="w-4 h-4" />
            </a>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-[1px] h-12 bg-gradient-to-b from-transparent via-[hsl(0,0%,30%)] to-transparent"
          />
        </motion.div>
      </section>

      {/* ─── PROJECTS ─── */}
      <section id="projects" className="px-6 py-24 md:py-32">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            <motion.p
              variants={fadeUp}
              custom={0}
              className="text-xs tracking-[0.35em] uppercase text-[hsl(0,75%,50%)] font-medium mb-4"
            >
              Selected Work
            </motion.p>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className="text-3xl sm:text-5xl font-bold tracking-tight mb-16"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Projects
            </motion.h2>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2">
            {projects.map((project, i) => (
              <motion.div
                key={project.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={fadeUp}
                custom={i}
              >
                {project.external ? (
                  <a
                    href={project.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                  >
                    <ProjectCard project={project} />
                  </a>
                ) : (
                  <Link to={project.href} className="group block">
                    <ProjectCard project={project} />
                  </Link>
                )}
              </motion.div>
            ))}

            {/* Placeholder card for future projects */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={fadeUp}
              custom={2}
              className="border border-dashed border-[hsl(0,0%,18%)] p-8 sm:p-10 flex items-center justify-center min-h-[220px]"
            >
              <p className="text-sm text-[hsl(0,0%,30%)] tracking-widest uppercase">
                More coming soon
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── ABOUT ─── */}
      <section className="px-6 py-24 md:py-32 border-t border-[hsl(0,0%,12%)]">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            <motion.p
              variants={fadeUp}
              custom={0}
              className="text-xs tracking-[0.35em] uppercase text-[hsl(0,75%,50%)] font-medium mb-4"
            >
              About
            </motion.p>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className="text-3xl sm:text-5xl font-bold tracking-tight mb-10"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              The Mission
            </motion.h2>
            <motion.div
              variants={fadeUp}
              custom={2}
              className="space-y-6 text-[hsl(0,0%,55%)] text-base sm:text-lg leading-relaxed"
            >
              <p>
                My Goal is to build products and communities that cut through the noise. In a world drowning in AI-generated content, algorithm manipulation, and performative nonsense — I choose substance. With a single overarching theme: <span className="text-[hsl(0,0%,92%)] font-medium">Deliver Goodness; Achieve Greatness.</span>
              </p>
              <p>
                Every project I create starts with these questions: <span className="text-[hsl(0,0%,92%)] font-medium">Does this actually help someone? Would I use it?</span> If the answer isn't a clear yes, it doesn't ship.
              </p>
              <p>
                This is my corner of the internet. No SEO filler, no growth hacks, no VC-backed vaporware. Just real work, real words, real impact.
              </p>
              <p className="text-[hsl(0,0%,92%)] font-medium italic">
                Do Good. Be Great, my Friends.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── SOCIAL ─── */}
      <section className="px-6 py-24 md:py-32 border-t border-[hsl(0,0%,12%)]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            <motion.p
              variants={fadeUp}
              custom={0}
              className="text-xs tracking-[0.35em] uppercase text-[hsl(0,75%,50%)] font-medium mb-4"
            >
              Connect
            </motion.p>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className="text-3xl sm:text-5xl font-bold tracking-tight mb-12"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Find Me Online
            </motion.h2>

            <motion.div
              variants={fadeUp}
              custom={2}
              className="flex flex-wrap justify-center gap-4"
            >
              {socials.map((s) => {
                const IconComponent = s.label === "X" ? XIcon : s.icon;
                const isDisabled = s.comingSoon;
                const Tag = isDisabled ? "div" : "a";
                const linkProps = isDisabled
                  ? {}
                  : { href: s.href, target: "_blank" as const, rel: "noopener noreferrer" };
                return (
                  <Tag
                    key={s.label}
                    {...linkProps}
                    className={`group flex items-center gap-3 border border-[hsl(0,0%,18%)] px-6 py-3 transition-all duration-300 ${
                      isDisabled
                        ? "opacity-50 cursor-default"
                        : "hover:border-[hsl(0,75%,50%)] hover:text-[hsl(0,75%,50%)] cursor-pointer"
                    }`}
                    aria-label={s.label}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span className="text-sm tracking-wide">{s.label}</span>
                    {isDisabled && (
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[hsl(0,0%,35%)] ml-1">
                        Coming Soon
                      </span>
                    )}
                  </Tag>
                );
              })}
            </motion.div>

            <motion.div variants={fadeUp} custom={3} className="mt-8">
              <a
                href="mailto:support@realwordsonly.com"
                className="inline-flex items-center gap-3 text-sm tracking-wide text-[hsl(0,0%,55%)] hover:text-[hsl(0,75%,50%)] transition-colors duration-300"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="M22 4L12 13L2 4" />
                </svg>
                support@realwordsonly.com
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── SUPPORT ─── */}
      <section className="px-6 py-24 md:py-32 border-t border-[hsl(0,0%,12%)]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            <motion.p
              variants={fadeUp}
              custom={0}
              className="text-xs tracking-[0.35em] uppercase text-[hsl(0,75%,50%)] font-medium mb-4"
            >
              Support
            </motion.p>
            <motion.h2
              variants={fadeUp}
              custom={1}
              className="text-3xl sm:text-5xl font-bold tracking-tight mb-6"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Keep It Real
            </motion.h2>
            <motion.p
              variants={fadeUp}
              custom={2}
              className="text-[hsl(0,0%,55%)] text-base sm:text-lg leading-relaxed max-w-xl mx-auto mb-10"
            >
              This platform runs on principle, not ads. If you believe in honest conversation and real community, your support keeps the lights on and the mission alive.
            </motion.p>
            <motion.div variants={fadeUp} custom={3}>
              <DonationDialog
                variant="outline"
                size="lg"
                className="border-[hsl(0,0%,25%)] bg-transparent text-[hsl(0,0%,92%)] hover:border-[hsl(0,75%,50%)] hover:text-[hsl(0,75%,50%)] hover:bg-transparent tracking-widest uppercase text-sm px-10 py-5 h-auto rounded-none transition-all duration-300"
                buttonText="Support the Mission"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── CONTACT ─── */}
      <ContactForm />

      {/* ─── FOOTER ─── */}
      <footer className="px-6 py-12 border-t border-[hsl(0,0%,12%)]">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p
            className="text-sm font-bold tracking-tight"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Real Words Only<span className="text-[hsl(0,75%,50%)]">.</span>
          </p>
          <p className="text-xs text-[hsl(0,0%,35%)]">
            © {new Date().getFullYear()} All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

function ProjectCard({ project }: { project: typeof projects[number] }) {
  return (
    <div className="border border-[hsl(0,0%,14%)] p-8 sm:p-10 transition-all duration-300 group-hover:border-[hsl(0,75%,50%)] group-hover:bg-[hsl(0,0%,6%)] min-h-[220px] flex flex-col justify-between">
      <div>
        <span className="text-[10px] tracking-[0.3em] uppercase text-[hsl(0,0%,40%)] block mb-4">
          {project.tag}
        </span>
        <h3
          className="text-2xl sm:text-3xl font-bold tracking-tight mb-3"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {project.title}
        </h3>
        <p className="text-sm text-[hsl(0,0%,50%)] leading-relaxed max-w-md">
          {project.description}
        </p>
      </div>
      <div className="mt-6 flex items-center gap-2 text-xs tracking-widest uppercase text-[hsl(0,0%,40%)] group-hover:text-[hsl(0,75%,50%)] transition-colors duration-300">
        View Project <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </div>
  );
}

export default Index;
